const throttle = require('lodash/throttle');

const className = 'is-scrolling';
const STATE_ON = 'on';
const STATE_OFF = 'off';

/**
 * Creates a new scrollEndEventDispatcher. An object with this function:
 * - watch()
 *
 * Its goal is to wait a timeout and fire a scrollend pseudoevent
 * @param {Function} onEndFunction - scrollend handler
 * @returns {Object} scrollEndEventDispatcher
 */
function createScrollEndEventDispatcher(onEndFunction) {
  const SCROLLEND_EVENT_TIMEOUT = 500;
  const SCROLLEND_RESET_TIMEOUT = 200;

  let timeoutScrollStopId = null;

  /**
   * Cancels the execution of `onEndFunction` from a previous scheduling.
   * @returns {void}
   */
  function cancelEventDispatch() {
    clearTimeout(timeoutScrollStopId);
  }

  /**
   * Schedule the execution of `onEndFunction` after event timeout fires.
   * @returns {void}
   */
  function setupEventDispatch() {
    timeoutScrollStopId = setTimeout(() => {
      onEndFunction();
      timeoutScrollStopId = null;
    }, SCROLLEND_EVENT_TIMEOUT);
  }

  return {
    /**
     * If a new watcher is fired after SCROLLEND_RESET_TIMEOUT, then the event
     * dispatch will be reset.
     * Otherwise, the timeout will finish between 300 and 500 milliseconds after
     * the last watch command that setup the event dispatch.
     * In that manner it will avoid to flickers cause by an early scrollEnd
     * event.
     * @returns {void}
     */
    watch: throttle(() => {
      cancelEventDispatch();
      setupEventDispatch();
    }, SCROLLEND_RESET_TIMEOUT),
  };
}

function createScrollMonitor(element) {
  const classlistTarget =
    element === document ? element.documentElement : element;
  const scrollEndEventDispatcher =
    createScrollEndEventDispatcher(handleScrollEnd);

  let state = STATE_OFF;
  let lastScrollY = 0;
  let scrollDirection = null; // 'up' | 'down'
  const DIRECTION_THRESHOLD = 10;

  /**
   * Set the monitor in scrolling state
   */
  function on() {
    state = STATE_ON;
    classlistTarget.classList.add(className);
  }

  /**
   * Set the monitor out of scrolling state
   */
  function off() {
    state = STATE_OFF;
    classlistTarget.classList.remove(className);
  }

  /**
   * Check if monitor is in scrolling state
   * @returns {boolean}
   */
  function isScrolling() {
    return state === STATE_ON;
  }

  /**
   * Scroll event handler function
   */
  function handleScroll() {
    if (!isScrolling()) on();
    scrollEndEventDispatcher.watch();

    // Direction detection
    const currentY = element === document ? window.scrollY : element.scrollTop;
    const diff = currentY - lastScrollY;

    if (Math.abs(diff) >= DIRECTION_THRESHOLD) {
      const newDirection = diff > 0 ? 'down' : 'up';
      if (newDirection !== scrollDirection) {
        scrollDirection = newDirection;
        classlistTarget.classList.toggle(
          'is-scrolling-down',
          scrollDirection === 'down'
        );
        classlistTarget.classList.toggle(
          'is-scrolling-up',
          scrollDirection === 'up'
        );
      }
      lastScrollY = currentY;
    }

    // Always show at top
    if (currentY <= 0) {
      classlistTarget.classList.remove('is-scrolling-down');
      classlistTarget.classList.add('is-scrolling-up');
      scrollDirection = 'up';
      lastScrollY = 0;
    }
  }

  /**
   * Scrollend pseudoevent handler function
   */
  function handleScrollEnd() {
    off();
  }

  // Scroll will be monitorized during whole lifecycle
  // eslint-disable-next-line listeners/no-missing-remove-event-listener
  element.addEventListener('scroll', handleScroll, { passive: true });

  return {
    on,
    off,
    isScrolling,
    getDirection: () => scrollDirection,
  };
}

let mainMonitor = null;

module.exports = {
  createScrollMonitor,
  init: function init() {
    if (mainMonitor) return;
    mainMonitor = createScrollMonitor(document);
  },
  isScrolling: function isScrolling() {
    if (!mainMonitor) throw new Error('Not active monitoring');
    return mainMonitor.isScrolling();
  },
};
